import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import ProductCard from '@/components/ProductCard';
import Spinner from '@/components/Spinner';
import { API } from '@/lib/utils';
import { useSEO } from '@/lib/useSEO';
import { ArrowLeft, FunnelSimple } from '@phosphor-icons/react';

export default function EmergencyTypeDetail() {
  const { slug } = useParams();
  const [type, setType] = useState(null);
  const [products, setProducts] = useState(null);
  const [catFilter, setCatFilter] = useState('');
  const [categories, setCategories] = useState([]);

  useSEO({
    title: type ? `${type.name} — noodpakket producten` : null,
    description: type ? `${type.description} Bekijk de beste producten voor ${type.name.toLowerCase()} en bestel direct bij jouw favoriete Nederlandse shop.` : null,
    image: type?.image,
    path: `/noodsituaties/${slug}`,
  });

  useEffect(() => {
    setType(null); setProducts(null);
    axios.get(`${API}/emergency-types/${slug}`).then((r) => setType(r.data));
    axios.get(`${API}/products?emergency_type=${slug}&limit=100`).then((r) => setProducts(Array.isArray(r.data) ? r.data : [])).catch(console.error);
    axios.get(`${API}/categories`).then((r) => setCategories(Array.isArray(r.data) ? r.data : [])).catch(console.error);
  }, [slug]);

  if (!type) return <Spinner label="Noodsituatie laden..." />;

  const filtered = catFilter ? (products || []).filter((p) => p.category === catFilter) : products || [];
  const availableCats = new Set((products || []).map((p) => p.category));

  return (
    <div data-testid={`page-emergency-${slug}`}>
      {/* Hero */}
      <section className="relative border-b border-border overflow-hidden">
        <div className="absolute inset-0">
          <img src={type.image} alt={type.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: `linear-gradient(90deg, ${type.color}f2 0%, ${type.color}a0 60%, transparent 100%)` }} />
        </div>
        <div className="relative container py-20 lg:py-28 text-background">
          <Link to="/noodsituaties" className="inline-flex items-center gap-1 text-sm font-medium text-background/80 hover:text-background mb-6">
            <ArrowLeft size={16} /> Alle noodsituaties
          </Link>
          <div className="overline text-background/70 mb-4">Noodsituatie</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[0.95] max-w-3xl">
            {type.name}
          </h1>
          <p className="mt-6 text-lg text-background/80 max-w-2xl leading-relaxed">
            {type.description}
          </p>
        </div>
      </section>

      <section className="container py-12 lg:py-16">
        {/* Category filter pills */}
        <div className="flex items-center gap-3 mb-8 overflow-x-auto pb-2">
          <div className="flex items-center gap-2 text-sm font-semibold whitespace-nowrap">
            <FunnelSimple weight="bold" size={16} /> Filter:
          </div>
          <button
            onClick={() => setCatFilter('')}
            data-testid="filter-all"
            className={`whitespace-nowrap px-4 py-2 rounded-sm text-sm font-medium border transition-colors ${!catFilter ? 'bg-primary text-primary-foreground border-primary' : 'bg-card border-border hover:border-primary'}`}
          >
            Alles ({(products || []).length})
          </button>
          {(categories || []).filter(c => availableCats.has(c.slug)).map((c) => (
            <button
              key={c.slug}
              onClick={() => setCatFilter(c.slug)}
              data-testid={`filter-${c.slug}`}
              className={`whitespace-nowrap px-4 py-2 rounded-sm text-sm font-medium border transition-colors ${catFilter === c.slug ? 'bg-primary text-primary-foreground border-primary' : 'bg-card border-border hover:border-primary'}`}
            >
              {c.name}
            </button>
          ))}
        </div>

        {products === null ? (
          <Spinner label="Producten laden..." />
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground">Geen producten gevonden in deze categorie.</div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {(filtered || []).map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
          </div>
        )}
      </section>
    </div>
  );
}
